import React, { useState } from "react";
import { Sparkles, Loader2, ArrowRight, Baby, HeartHandshake } from "lucide-react";
import api from "../../lib/api";

// Live taste of Lumi, WYVN's dedicated care coach — real replies, IP rate-limited.
export function LumiDemo({ onJoin }) {
  const [weeks, setWeeks] = useState(12);
  const [role, setRole] = useState("her"); // her | partner
  const [note, setNote] = useState(null);
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [limited, setLimited] = useState(false);

  const tryLumi = async () => {
    if (busy) return;
    setBusy(true); setNote(null);
    try {
      const r = await api.lumiDemo(Number(weeks) || 1, role);
      setNote(r.note); setMeta({ week: r.week, trimester: r.trimester, babySize: r.babySize });
      if (r.limited) setLimited(true);
    } catch (e) {
      setNote("I'm taking a quiet breath — try me again in a moment. 💛");
    } finally { setBusy(false); }
  };

  return (
    <section id="try-lumi" className="relative mx-auto max-w-4xl px-5 py-24 lg:px-8" data-testid="lumi-demo">
      <div className="pointer-events-none absolute left-1/2 top-24 h-64 w-[640px] -translate-x-1/2 rounded-full blur-[130px]" style={{ background: "#E7A6C41F" }} />
      <div className="relative mb-10 text-center">
        <p className="eyebrow" style={{ color: "#E7A6C4" }}>Meet Lumi</p>
        <h2 className="mt-5 text-3xl font-semibold text-mist sm:text-4xl">Feel a real note from your care coach</h2>
        <p className="mx-auto mt-4 max-w-xl text-mist-muted">No sign-up needed. Tell Lumi how far along you are and read the warm, personal note she'd send you — or your partner — today.</p>
      </div>

      <div className="glass relative overflow-hidden rounded-[2rem] p-5 sm:p-7">
        <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full blur-[90px]" style={{ background: "#E7A6C433" }} />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-end">
          <div className="flex-1">
            <label className="mb-1.5 block text-xs font-medium text-mist-muted">How many weeks along?</label>
            <input type="number" min="1" max="42" value={weeks} onChange={(e) => setWeeks(e.target.value)} data-testid="lumi-weeks-input" className="aeva-input w-full" />
          </div>
          <div className="flex-1">
            <label className="mb-1.5 block text-xs font-medium text-mist-muted">Write it for…</label>
            <div className="flex gap-2">
              <button onClick={() => setRole("her")} data-testid="lumi-role-her" className={`flex-1 rounded-xl px-3 py-2.5 text-sm font-medium transition-all ${role === "her" ? "text-[#05101f]" : "border border-white/10 text-mist-muted"}`} style={role === "her" ? { background: "#E7A6C4" } : undefined}><Baby size={14} className="mb-0.5 mr-1 inline" /> Me</button>
              <button onClick={() => setRole("partner")} data-testid="lumi-role-partner" className={`flex-1 rounded-xl px-3 py-2.5 text-sm font-medium transition-all ${role === "partner" ? "text-[#05101f]" : "border border-white/10 text-mist-muted"}`} style={role === "partner" ? { background: "#E7A6C4" } : undefined}><HeartHandshake size={14} className="mb-0.5 mr-1 inline" /> My partner</button>
            </div>
          </div>
          <button onClick={tryLumi} disabled={busy} data-testid="lumi-try-btn" className="btn-primary flex items-center justify-center gap-2 disabled:opacity-50 sm:w-40">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <>Ask Lumi <Sparkles size={15} /></>}
          </button>
        </div>

        {(note || busy) && (
          <div className="relative mt-5 rounded-2xl border p-5" style={{ borderColor: "#E7A6C433", background: "#E7A6C40D" }} data-testid="lumi-demo-note">
            <div className="flex items-center gap-2" style={{ color: "#E7A6C4" }}>
              <span className="flex h-7 w-7 items-center justify-center rounded-full font-display text-sm font-bold" style={{ background: "#E7A6C422" }}>L</span>
              <span className="text-xs font-semibold uppercase tracking-wider">Lumi{meta ? ` · Week ${meta.week} · ${meta.trimester}` : ""}</span>
            </div>
            {busy ? <div className="mt-3 flex items-center gap-2 text-sm text-mist-muted"><Loader2 size={14} className="animate-spin" /> Lumi is writing your note…</div>
                  : <p className="mt-3 text-base leading-relaxed text-mist">{note}</p>}
            {meta && !busy && <p className="mt-3 text-xs text-mist-muted">Baby is about {meta.babySize} right now.</p>}
          </div>
        )}

        <button onClick={onJoin} data-testid="lumi-demo-cta" className="mt-5 flex w-full items-center justify-center gap-2 rounded-full border border-white/10 bg-white/[0.03] py-3 text-sm font-medium text-mist transition-colors hover:border-white/20">
          {limited ? "Start free to keep your daily notes" : "Get a note like this every morning — start free"} <ArrowRight size={15} />
        </button>
      </div>
    </section>
  );
}
